import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Tooltip,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { appConfig, formatFileSize, getMimeType } from '@/config/app-config';

interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

interface FileUploaderProps {
  onUpload: (files: File[]) => Promise<void>;
  isUploading: boolean;
  uploadProgresses: UploadProgress[];
  onClearProgresses: () => void;
  disabled?: boolean;
  existingFileNames?: string[];
}

/**
 * ファイルアップローダーコンポーネント
 * ドラッグ＆ドロップでファイルをアップロード
 */
const FileUploader: React.FC<FileUploaderProps> = ({
  onUpload,
  isUploading,
  uploadProgresses,
  onClearProgresses,
  disabled = false,
  existingFileNames = [],
}) => {
  const [stagedFiles, setStagedFiles] = React.useState<File[]>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [conflictingFiles, setConflictingFiles] = React.useState<File[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setStagedFiles(prev => {
        // 重複ファイル（同名）を除去して新しいファイルを追加 (Unicode NFC正規化して比較)
        const filteredPrev = prev.filter(p => !acceptedFiles.some(a => a.name.normalize('NFC') === p.name.normalize('NFC')));
        return [...filteredPrev, ...acceptedFiles];
      });
    }
  }, []);

  const handleRemoveStagedFile = useCallback((index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const isConflicting = useCallback((fileName: string) => {
    const normalized = fileName.normalize('NFC');
    return existingFileNames.some(existing => existing.normalize('NFC') === normalized);
  }, [existingFileNames]);

  const handleUploadClick = useCallback(async () => {
    if (stagedFiles.length === 0) return;

    const normalizedExisting = new Set(existingFileNames.map(name => name.normalize('NFC')));
    const conflicts = stagedFiles.filter(file => normalizedExisting.has(file.name.normalize('NFC')));

    if (conflicts.length > 0) {
      setConflictingFiles(conflicts);
      setConfirmDialogOpen(true);
    } else {
      await onUpload(stagedFiles);
      setStagedFiles([]);
    }
  }, [stagedFiles, existingFileNames, onUpload]);

  const handleOverwriteAll = useCallback(async () => {
    setConfirmDialogOpen(false);
    await onUpload(stagedFiles);
    setStagedFiles([]);
  }, [stagedFiles, onUpload]);

  const handleSkipDuplicates = useCallback(async () => {
    setConfirmDialogOpen(false);
    const normalizedExisting = new Set(existingFileNames.map(name => name.normalize('NFC')));
    const nonDuplicateFiles = stagedFiles.filter(file => !normalizedExisting.has(file.name.normalize('NFC')));
    if (nonDuplicateFiles.length > 0) {
      await onUpload(nonDuplicateFiles);
    }
    setStagedFiles([]);
  }, [stagedFiles, existingFileNames, onUpload]);

  const handleCancelDialog = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    disabled: disabled || isUploading,
    accept: appConfig.allowedExtensions.reduce((acc: Record<string, string[]>, ext: string) => {
      const mime = getMimeType(ext);
      acc[mime] = [...(acc[mime] || []), ext];
      return acc;
    }, {} as Record<string, string[]>),
    maxSize: appConfig.maxFileSizeMB * 1024 * 1024,
  });

  const getStatusIcon = (status: UploadProgress['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <InsertDriveFileIcon />;
    }
  };

  const hasCompletedUploads = uploadProgresses.some(p => p.status === 'completed' || p.status === 'error');

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
        ファイルアップロード
      </Typography>

      {/* ドロップゾーン */}
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragReject ? 'error.main' : isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s',
          opacity: disabled || isUploading ? 0.5 : 1,
          '&:hover': {
            bgcolor: disabled || isUploading ? 'background.paper' : 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {isDragActive
            ? 'ここにドロップ'
            : 'ファイルをドラッグ＆ドロップ'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          または クリックして選択
        </Typography>
      </Box>

      {/* 制限情報 */}
      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={`最大${appConfig.maxFileSizeMB}MB`}
          size="small"
          variant="outlined"
        />
        {(() => {
          const exts = appConfig.allowedExtensions;
          const maxVisible = 5;
          const visible = exts.slice(0, maxVisible);
          const hidden = exts.slice(maxVisible);
          const label = hidden.length > 0
            ? `${visible.join(', ')} ...+${hidden.length}`
            : visible.join(', ');
          return (
            <Tooltip title={exts.join(', ')} placement="bottom-start">
              <Chip label={label} size="small" variant="outlined" />
            </Tooltip>
          );
        })()}
      </Box>

      {/* ステージングされているファイル一覧 */}
      {stagedFiles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontWeight="bold">
            アップロード待ちファイル ({stagedFiles.length})
          </Typography>
          <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, p: 0.5 }}>
            {stagedFiles.map((file, index) => (
              <ListItem
                key={file.name}
                secondaryAction={
                  <IconButton edge="end" size="small" onClick={() => handleRemoveStagedFile(index)} disabled={isUploading}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
                disablePadding
              >
                <ListItemIcon sx={{ minWidth: 36, pl: 1 }}>
                  <InsertDriveFileIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, maxWidth: '200px' }}>
                      <Typography variant="body2" noWrap sx={{ flexShrink: 1 }}>
                        {file.name}
                      </Typography>
                      {isConflicting(file.name) && (
                        <Chip
                          label="上書き"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', px: 0.5, flexShrink: 0 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={formatFileSize(file.size)}
                  sx={{ my: 0.5 }}
                />
              </ListItem>
            ))}
          </List>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleUploadClick}
            disabled={isUploading}
            startIcon={<CloudUploadIcon />}
            sx={{ mt: 1.5 }}
          >
            アップロードを実行
          </Button>
        </Box>
      )}

      {/* アップロード進捗 */}
      {uploadProgresses.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              アップロード状況
            </Typography>
            {hasCompletedUploads && (
              <IconButton size="small" onClick={onClearProgresses}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <List dense disablePadding>
            {uploadProgresses.map((progress, index) => (
              <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {getStatusIcon(progress.status)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" noWrap>
                      {progress.fileName}
                    </Typography>
                  }
                  secondary={
                    progress.status === 'error' ? (
                      <Typography variant="caption" color="error">
                        {progress.error}
                      </Typography>
                    ) : progress.status === 'uploading' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progress.progress}
                          sx={{ flexGrow: 1 }}
                        />
                        <Typography variant="caption">
                          {progress.progress}%
                        </Typography>
                      </Box>
                    ) : progress.status === 'completed' ? (
                      <Typography variant="caption" color="success.main">
                        完了
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        待機中...
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* 上書き確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="overwrite-dialog-title"
      >
        <DialogTitle id="overwrite-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          同名ファイルの上書き確認
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            以下のファイルは現在のフォルダに既に存在します。上書きしますか？
          </DialogContentText>
          <List
            dense
            sx={{
              maxHeight: 180,
              overflow: 'auto',
              bgcolor: 'action.hover',
              borderRadius: 1,
              p: 0.5,
              mb: 1.5,
            }}
          >
            {conflictingFiles.map(file => (
              <ListItem key={file.name}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <InsertDriveFileIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={formatFileSize(file.size)}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
          {conflictingFiles.length < stagedFiles.length ? (
            <Typography variant="caption" color="text.secondary">
              ※ 「重複をスキップ」を選択すると、新規ファイル（{stagedFiles.length - conflictingFiles.length}件）のみをアップロードします。
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              ※ アップロード対象のすべてのファイル（{conflictingFiles.length}件）が既に存在しています。
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={handleCancelDialog} color="inherit">
            キャンセル
          </Button>
          {conflictingFiles.length < stagedFiles.length && (
            <Button
              onClick={handleSkipDuplicates}
              variant="outlined"
              color="primary"
            >
              重複をスキップしてアップロード ({stagedFiles.length - conflictingFiles.length}件)
            </Button>
          )}
          <Button
            onClick={handleOverwriteAll}
            variant="contained"
            color="warning"
          >
            上書きしてアップロード ({stagedFiles.length}件)
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FileUploader;
